/**
 * Which organizations this account belongs to, and choosing one.
 *
 * Custom claims hold ONE orgId. A nurse who works at two hospitals on the
 * platform therefore has to choose, and the choice has to be made before
 * the app loads anything — the alternative is landing in whichever
 * organization the last session used, which is wrong half the time and
 * invisible when it is.
 */

import { call } from '../lib/callable';

export interface MyOrganization {
  orgId: string;
  name: string;
  roles: string[];
}

interface RawOrg {
  orgId?: string;
  id?: string;
  name?: string;
  orgName?: string;
  roles?: unknown;
}

function toOrg(raw: RawOrg): MyOrganization | null {
  const orgId = raw.orgId ?? raw.id;
  if (!orgId) return null;
  return {
    orgId,
    name: raw.orgName ?? raw.name ?? orgId,
    roles: Array.isArray(raw.roles) ? raw.roles.filter((r): r is string => typeof r === 'string') : [],
  };
}

/** The active memberships of the signed-in account. */
export async function listMyOrganizations(): Promise<MyOrganization[]> {
  const res = await call<Record<string, never>, { organizations?: RawOrg[] }>('getUserOrganizations', {});
  return (res.organizations ?? []).map(toOrg).filter((o): o is MyOrganization => o !== null);
}

/** Point the session at one organization. The caller refreshes the token. */
export async function chooseOrganization(orgId: string): Promise<void> {
  await call<{ orgId: string }, unknown>('selectOrganization', { orgId });
}

/** What to do after signing in, given the memberships. */
export function landing(orgs: readonly MyOrganization[]): { kind: 'none' } | { kind: 'one'; orgId: string } | { kind: 'choose' } {
  if (orgs.length === 0) return { kind: 'none' };
  if (orgs.length === 1) return { kind: 'one', orgId: orgs[0]!.orgId };
  return { kind: 'choose' };
}
